import { useState, useEffect } from 'react';
import api from '../api';
import {
  Trophy,
  Plus,
  Trash2,
  RefreshCw,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  GripVertical,
  Undo,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORY_LABELS = {
  sessions: 'Sessions',
  hours: 'Hours Played',
  points: 'Points Earned',
  games: 'Game Variety',
  tiers: 'Tier Milestones',
  visits: 'Casino Visits',
};

const REQUIREMENT_TYPES = [
  { value: 'sessions', label: 'Sessions' },
  { value: 'hours', label: 'Hours' },
  { value: 'points', label: 'Points' },
  { value: 'games', label: 'Games Played' },
  { value: 'level', label: 'Level/Tier' },
  { value: 'visits', label: 'Visits' },
];

const ICONS = [
  'trophy', 'medal', 'star', 'diamond', 'shield', 'shield-checkmark',
  'time', 'timer', 'hourglass', 'calendar', 'calendar-outline',
  'footsteps', 'grid', 'apps', 'flame', 'flash', 'ribbon',
];

// Helper function to generate requirement label
const generateRequirementLabel = (type, value) => {
  switch (type) {
    case 'sessions':
      return `Complete ${value} session${value !== 1 ? 's' : ''}`;
    case 'hours':
      return `Play ${value} hour${value !== 1 ? 's' : ''}`;
    case 'points':
      return `Earn ${Number(value).toLocaleString()} point${value !== 1 ? 's' : ''}`;
    case 'games':
      return `Play ${value} different game${value !== 1 ? 's' : ''}`;
    case 'level':
      const tierNames = { 4: 'Elite', 7: 'Ambassador', 10: 'Boardmember', 13: 'Chairman', 14: 'Legend' };
      return `Reach ${tierNames[value] || `Level ${value}`}`;
    case 'visits':
      return `Visit the casino ${value} day${value !== 1 ? 's' : ''}`;
    default:
      return `Complete requirement`;
  }
};

export default function Achievements() {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showNewForm, setShowNewForm] = useState(false);
  const [newAchievement, setNewAchievement] = useState({
    id: '',
    name: '',
    description: '',
    icon: 'trophy',
    xp: 100,
    requirementType: 'sessions',
    requirementValue: 1,
    requirementLabel: '',
    category: 'sessions',
  });
  const [expandedCategories, setExpandedCategories] = useState(Object.keys(CATEGORY_LABELS));

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/achievements');
      setAchievements(response.data.achievements || []);
    } catch (err) {
      toast.error('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAchievements(); }, []);

  const handleEdit = (achievement) => {
    setEditingId(achievement.id);
    setEditForm({ ...achievement });
  };

  const handleSave = async () => {
    try {
      // Auto-generate label if not set or if type/value changed
      const updatedForm = {
        ...editForm,
        requirementLabel: editForm.requirementLabel || generateRequirementLabel(editForm.requirementType, editForm.requirementValue),
      };
      await api.put(`/admin/achievements/${editingId}`, updatedForm);
      toast.success('Achievement updated');
      setEditingId(null);
      fetchAchievements();
    } catch (err) {
      toast.error('Failed to update achievement');
    }
  };

  const handleToggleActive = async (achievement) => {
    try {
      if (achievement.isActive) {
        await api.delete(`/admin/achievements/${achievement.id}`);
        toast.success('Achievement hidden');
      } else {
        await api.post(`/admin/achievements/${achievement.id}/restore`);
        toast.success('Achievement restored');
      }
      fetchAchievements();
    } catch (err) {
      toast.error('Failed to update achievement');
    }
  };

  const handleCreate = async () => {
    if (!newAchievement.id || !newAchievement.name) {
      toast.error('ID and Name are required');
      return;
    }
    try {
      await api.post('/admin/achievements', {
        ...newAchievement,
        requirementLabel: newAchievement.requirementLabel || generateRequirementLabel(newAchievement.requirementType, newAchievement.requirementValue),
      });
      toast.success('Achievement created');
      setShowNewForm(false);
      setNewAchievement({
        id: '',
        name: '',
        description: '',
        icon: 'trophy',
        xp: 100,
        requirementType: 'sessions',
        requirementValue: 1,
        requirementLabel: '',
        category: 'sessions',
      });
      fetchAchievements();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create achievement');
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const [recalculating, setRecalculating] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleRecalculate = async () => {
    if (!confirm('This will award any NEW achievements users have earned. Already earned achievements will NOT be removed. Continue?')) {
      return;
    }
    
    try {
      setRecalculating(true);
      const response = await api.post('/admin/achievements/recalculate');
      const { summary, details } = response.data;
      
      toast.success(`Recalculated! ${summary.achievementsAwarded} achievements awarded to ${summary.usersUpdated} users`);
      
      if (details && details.length > 0) {
        console.log('Recalculation details:', details);
      }
    } catch (err) {
      toast.error('Failed to recalculate achievements');
      console.error(err);
    } finally {
      setRecalculating(false);
    }
  };

  const handleHardReset = async () => {
    if (!confirm('⚠️ WARNING: This will DELETE ALL earned achievements for ALL users and recalculate from scratch!\n\nThis action cannot be undone. Are you sure?')) {
      return;
    }
    if (!confirm('🚨 FINAL CONFIRMATION: All user achievement progress will be wiped. Type "RESET" in the next prompt to confirm.')) {
      return;
    }
    const confirmText = prompt('Type RESET to confirm:');
    if (confirmText !== 'RESET') {
      toast.error('Reset cancelled - confirmation text did not match');
      return;
    }
    
    try {
      setResetting(true);
      const response = await api.post('/admin/achievements/reset');
      const { summary, details } = response.data;
      
      toast.success(`Hard reset complete! Deleted ${summary.deletedRecords} records, awarded ${summary.achievementsAwarded} to ${summary.usersUpdated} users`);
      
      if (details && details.length > 0) {
        console.log('Reset details:', details);
      }
    } catch (err) {
      toast.error('Failed to reset achievements');
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  const groupedAchievements = achievements.reduce((acc, a) => {
    const cat = a.category || 'sessions';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Achievements</h1>
          <span className="text-gray-500">({achievements.length})</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleHardReset}
            disabled={resetting || recalculating}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-500 disabled:opacity-50"
            title="Delete ALL earned achievements and recalculate from scratch"
          >
            <Trash2 size={18} className={resetting ? 'animate-pulse' : ''} />
            {resetting ? 'Resetting...' : 'Hard Reset'}
          </button>
          <button
            onClick={handleRecalculate}
            disabled={recalculating || resetting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-500 disabled:opacity-50"
            title="Award new achievements (keeps existing)"
          >
            <RefreshCw size={18} className={recalculating ? 'animate-spin' : ''} />
            {recalculating ? 'Recalculating...' : 'Recalculate'}
          </button>
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400"
          >
            <Plus size={18} /> New Achievement
          </button>
          <button onClick={fetchAchievements} className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {Object.entries(CATEGORY_LABELS).map(([catKey, catLabel]) => {
          const catAchievements = groupedAchievements[catKey] || [];
          const isExpanded = expandedCategories.includes(catKey);
          const activeCount = catAchievements.filter(a => a.isActive).length;

          return (
            <div key={catKey} className="bg-gray-800 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(catKey)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-700/50"
              >
                <div className="flex items-center gap-3">
                  <Trophy className="text-amber-500" size={20} />
                  <span className="text-lg font-semibold text-white">{catLabel}</span>
                  <span className="text-sm text-gray-500">
                    ({activeCount}/{catAchievements.length} active)
                  </span>
                </div>
                {isExpanded ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
              </button>

              {isExpanded && catAchievements.length > 0 && (
                <div className="border-t border-gray-700">
                  {catAchievements.sort((a, b) => a.sortOrder - b.sortOrder).map((achievement) => (
                    <div
                      key={achievement.id}
                      className={`p-4 border-b border-gray-700 last:border-b-0 ${
                        !achievement.isActive ? 'opacity-50' : ''
                      }`}
                    >
                      {editingId === achievement.id ? (
                        /* Edit Mode */
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                              placeholder="Name"
                            />
                            <input
                              type="number"
                              value={editForm.xp}
                              onChange={(e) => setEditForm({ ...editForm, xp: Number(e.target.value) })}
                              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                              placeholder="XP Reward"
                            />
                          </div>
                          <input
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                            placeholder="Description"
                          />
                          <div className="grid grid-cols-4 gap-3">
                            <select
                              value={editForm.requirementType}
                              onChange={(e) => setEditForm({ ...editForm, requirementType: e.target.value })}
                              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                            >
                              {REQUIREMENT_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                            <input
                              type="number"
                              value={editForm.requirementValue}
                              onChange={(e) => setEditForm({ ...editForm, requirementValue: Number(e.target.value) })}
                              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                              placeholder="Required Value"
                            />
                            <select
                              value={editForm.category}
                              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                            >
                              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                            <select
                              value={editForm.icon}
                              onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })}
                              className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                            >
                              {ICONS.map(i => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-3 py-1 text-gray-400 hover:text-white"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSave}
                              className="flex items-center gap-1 px-3 py-1 bg-amber-500 text-black rounded hover:bg-amber-400"
                            >
                              <Save size={14} /> Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                              <Trophy className="text-amber-500" size={20} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">{achievement.name}</span>
                                <span className="text-amber-500 text-sm">+{achievement.xp} XP</span>
                                {achievement.isCustom && (
                                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Custom</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-400">{achievement.description}</p>
                              <p className="text-xs text-gray-500">
                                Requires: {achievement.requirementValue} {achievement.requirementType}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleActive(achievement)}
                              className={`p-2 rounded ${achievement.isActive ? 'text-green-400 hover:bg-green-500/10' : 'text-gray-500 hover:bg-gray-700'}`}
                              title={achievement.isActive ? 'Hide achievement' : 'Show achievement'}
                            >
                              {achievement.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                            <button
                              onClick={() => handleEdit(achievement)}
                              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                            >
                              <Edit2 size={18} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New Achievement Modal */}
      {showNewForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Create Achievement</h2>
              <button onClick={() => setShowNewForm(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">ID (unique)</label>
                  <input
                    value={newAchievement.id}
                    onChange={(e) => setNewAchievement({ ...newAchievement, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder="custom_achievement_1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Name</label>
                  <input
                    value={newAchievement.name}
                    onChange={(e) => setNewAchievement({ ...newAchievement, name: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder="Achievement Name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input
                  value={newAchievement.description}
                  onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Complete this challenge to earn XP"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">XP Reward</label>
                  <input
                    type="number"
                    value={newAchievement.xp}
                    onChange={(e) => setNewAchievement({ ...newAchievement, xp: Number(e.target.value) })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Category</label>
                  <select
                    value={newAchievement.category}
                    onChange={(e) => setNewAchievement({ ...newAchievement, category: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Icon</label>
                  <select
                    value={newAchievement.icon}
                    onChange={(e) => setNewAchievement({ ...newAchievement, icon: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    {ICONS.map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Requirement Type</label>
                  <select
                    value={newAchievement.requirementType}
                    onChange={(e) => setNewAchievement({ ...newAchievement, requirementType: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    {REQUIREMENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Required Value</label>
                  <input
                    type="number"
                    value={newAchievement.requirementValue}
                    onChange={(e) => setNewAchievement({ ...newAchievement, requirementValue: Number(e.target.value) })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>
              <button
                onClick={handleCreate}
                className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400"
              >
                Create Achievement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
